import React, { useEffect, useState, useMemo } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ForkKnife, Car, Train, Airplane, House, Storefront, GraduationCap, FirstAid, Bank, ArrowSquareOut } from "@phosphor-icons/react";
import api from "../lib/api";

const CATS = {
  property: { color: "#C8843C", label: "Le bien" },
  restaurant: { color: "#E8998D", label: "Restaurants" },
  shop: { color: "#8FBF9F", label: "Commerces" },
  transport: { color: "#8FB3DE", label: "Transports" },
  parking: { color: "#AEB6C4", label: "Parkings" },
  school: { color: "#EBCB8B", label: "Écoles" },
  health: { color: "#D9A7C7", label: "Santé" },
  bank: { color: "#7FB0B3", label: "Banques" },
  airport: { color: "#B3A5D9", label: "Aéroport" },
};

const FILTERS = ["restaurant", "shop", "transport", "parking", "school", "health", "bank", "airport"];

const catIcons = {
  restaurant: ForkKnife, shop: Storefront, transport: Train, parking: Car,
  school: GraduationCap, health: FirstAid, bank: Bank, airport: Airplane, property: House,
};

const iconSvg = (Icon, size) => renderToStaticMarkup(<Icon size={size} color="#ffffff" weight="bold" />);

const makeIcon = (cat, big = false) => {
  const color = CATS[cat]?.color || "#9CA3AF";
  const Icon = catIcons[cat] || House;
  const d = big ? 32 : 26;
  const html = `<div style="width:${d}px;height:${d}px;background:${color};border:2px solid #fff;border-radius:9999px;box-shadow:0 1px 6px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center">${iconSvg(Icon, big ? 17 : 14)}</div>`;
  return L.divIcon({ className: "", html, iconSize: [d, d], iconAnchor: [d / 2, d / 2] });
};

const PropertyMap = ({ property }) => {
  const [pois, setPois] = useState([]);
  const [active, setActive] = useState(() => FILTERS.reduce((acc, c) => ({ ...acc, [c]: true }), {}));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!property?.lat) return;
    setLoading(true);
    api.get(`/properties/${property.id}/pois`).then(({ data }) => setPois(data.pois || [])).finally(() => setLoading(false));
  }, [property?.id, property?.lat]);

  const grouped = useMemo(() => {
    const g = {};
    pois.forEach((p) => { (g[p.category] = g[p.category] || []).push(p); });
    return g;
  }, [pois]);

  if (!property?.lat) return null;
  const center = [property.lat, property.lng];

  return (
    <div data-testid="property-map">
      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((cat) => {
          const Icon = catIcons[cat];
          const on = active[cat];
          const color = CATS[cat].color;
          return (
            <button
              key={cat}
              data-testid={`poi-filter-${cat}`}
              onClick={() => setActive((a) => ({ ...a, [cat]: !a[cat] }))}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-all ${on ? "text-navy shadow-sm" : "bg-white text-muted-foreground border-navy/10 hover:border-navy/25"}`}
              style={on ? { backgroundColor: color + "33", borderColor: color } : {}}
            >
              <Icon size={16} weight="light" /> {CATS[cat].label}
              <span className="font-roboto font-bold opacity-80">{(grouped[cat] || []).length}</span>
            </button>
          );
        })}
      </div>

      <div className="h-[420px] w-full overflow-hidden rounded-2xl border border-navy/10 relative z-0">
        <MapContainer center={center} zoom={13} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <Marker position={center} icon={makeIcon("property", true)}>
            <Popup>{property.title?.fr || "Le bien"}</Popup>
          </Marker>
          {pois.filter((p) => active[p.category]).map((p, i) => (
            <Marker key={i} position={[p.lat, p.lng]} icon={makeIcon(p.category)}>
              <Popup>{p.name || CATS[p.category]?.label}</Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      {loading && <div className="text-xs text-muted-foreground mt-2">Chargement des points d'intérêt…</div>}

      {/* POI names list under the map */}
      {!loading && pois.length > 0 && (
        <div className="mt-6 space-y-5" data-testid="poi-list">
          {FILTERS.filter((c) => active[c] && (grouped[c] || []).length).map((cat) => {
            const Icon = catIcons[cat];
            const color = CATS[cat].color;
            return (
              <div key={cat} data-testid={`poi-list-${cat}`}>
                <div className="flex items-center gap-2 mb-2.5">
                  <span className="w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: color }}>
                    <Icon size={13} weight="bold" color="#fff" />
                  </span>
                  <span className="font-semibold text-navy text-sm">{CATS[cat].label}</span>
                  <span className="font-roboto text-xs text-muted-foreground">({grouped[cat].length})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {grouped[cat].map((p, i) => (
                    <a
                      key={i}
                      href={`https://www.google.com/maps/search/?api=1&query=${p.lat},${p.lng}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs bg-white border border-navy/10 rounded-full px-3 py-1.5 text-navy hover:border-gold hover:text-gold transition-colors"
                    >
                      {p.name || CATS[cat].label}
                      <ArrowSquareOut size={12} weight="bold" className="opacity-60" />
                    </a>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PropertyMap;
